import { AxiosError } from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useDownloadFile } from '.'
import { searchReviewService } from '../services'
import { RootState } from '../store'
import {
  clearSearchReviewState,
  SearchReview,
  setDetailsCsv,
  setSearchReviewLoading,
  setSelectedSearchReview,
} from '../store/reducers/searchReviewReducer'
interface UseSearchReviewDetailsProps {
  searchReviewId?: string
  preventFetch?: boolean
}

const useSearchReviewDetails = ({
  searchReviewId,
  preventFetch = false,
}: UseSearchReviewDetailsProps) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const downloadCsv = useDownloadFile()

  const selectedSearchReview: SearchReview | undefined = useSelector(
    (state: RootState) => state.searchReview.selected
  )

  const searchReviewLoading = useSelector(
    (state: RootState) => state.searchReview.loading
  )

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<AxiosError | Error>()
  const [isFetchDone, setIsFetchDone] = useState(false)

  const fetchSearchReviewDetails = useCallback(
    async (refreshForceCall = false) => {
      if (!searchReviewId) return
      if (!loading && ((!preventFetch && !isFetchDone) || refreshForceCall)) {
        try {
          setLoading(true)
          dispatch(setSearchReviewLoading(true))
          const response = await searchReviewService.getSearchReview(
            searchReviewId
          )
          dispatch(setSelectedSearchReview(response))
          dispatch(setSearchReviewLoading(false))
          setIsFetchDone(true)
        } catch (error) {
          console.error('axios get error', error)
          setError(error as AxiosError | Error)
        } finally {
          dispatch(setSearchReviewLoading(false))
          setLoading(false)
        }
      }
    },
    [dispatch, searchReviewId, isFetchDone, loading, preventFetch]
  )

  const refreshSearchReviewDetails = useCallback(() => {
    fetchSearchReviewDetails(true)
  }, [fetchSearchReviewDetails])

  useEffect(() => {
    if (searchReviewId) {
      fetchSearchReviewDetails()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchReviewId])

  const clearSearchReviewDetails = useCallback(() => {
    dispatch(clearSearchReviewState())
  }, [dispatch])

  interface UpdateSearchReviewDetailsProps {
    title: string
    description: string
  }
  const updateSearchReviewDetails = useCallback(
    async ({ title, description }: UpdateSearchReviewDetailsProps) => {
      if (!searchReviewId) {
        return
      }
      setLoading(true)
      const response = await searchReviewService.updateSearchReviewDetails({
        searchReviewId: searchReviewId ?? '',
        title,
        description,
        navigate,
      })
      dispatch(setSelectedSearchReview(response))
      setLoading(false)
    },
    [dispatch, navigate, searchReviewId]
  )

  const exportSearchReviewCsv = useCallback(
    (searchReviewId: string) => {
      if (searchReviewId) {
        searchReviewService
          .exportSearchReviewToCsv({ searchReviewId })
          .then((response: string) => {
            const filename = `${selectedSearchReview?.title}.csv`
            downloadCsv(response, filename)
            dispatch(setDetailsCsv(response))
          })
          .catch((error: AxiosError) => {
            console.error('Error fetching search review CSV data', error)
          })
      }
    },
    [dispatch, downloadCsv, selectedSearchReview?.title]
  )

  interface DeleteSearchReviewProps {
    searchReviewId: string
    navigate: NavigateFunction
  }

  const deleteSearchReview = useCallback(
    ({ searchReviewId, navigate }: DeleteSearchReviewProps) => {
      return searchReviewService
        .deleteSearchReview({
          searchReviewId: searchReviewId,
          navigate,
        })
        .then((response) => {
          return response
        })
        .catch((error: AxiosError | Error) => {
          console.error('axios fetch error', error)
          return error
        })
    },
    []
  )

  return {
    fetchSearchReviewDetails,
    refreshSearchReviewDetails,
    searchReviewLoading,
    error,
    selectedSearchReview,
    clearSearchReviewDetails,
    updateSearchReviewDetails,
    exportSearchReviewCsv,
    deleteSearchReview,
  }
}

export default useSearchReviewDetails
